import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Package, ShoppingBag, Users, TrendingUp, Plus, Edit2, Trash2, X,
  Tag, Grid, ToggleLeft, ToggleRight, Bell
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";

import { API_BASE as API } from "@/config/api";
import { compressImageFile, dataUrlByteSize, dataUrlToBlob, formatBytes } from "@/utils/compressImage";

const STATUS_OPTIONS = ["pending","confirmed","preparing","packed","out_for_delivery","delivered","cancelled","refunded"];
const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-700", confirmed: "bg-blue-100 text-blue-700",
  preparing: "bg-purple-100 text-purple-700", packed: "bg-indigo-100 text-indigo-700",
  out_for_delivery: "bg-orange-100 text-orange-700", delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700", refunded: "bg-gray-100 text-gray-700",
};

const HARDCODED_CATEGORIES = ["Chocolate Bouquets","Rose Bouquets","Money Bouquets","Gift Hampers","Haldi Favors / Shagun Cones","Customized Gift Boxes","Festival Gifts","Birthday Gifts","Anniversary Gifts","Wedding Gifts","Corporate Gifting"];

const EMPTY_PRODUCT = {
  name: "", description: "", price: "", original_price: "", category: "",
  stock: "10", tags: "", occasion: "", is_featured: false, is_bestseller: false,
  is_new: false, customizable: true,
};

const MAX_PRODUCT_IMAGES = 5;

const EMPTY_CATEGORY = { name: "", description: "", image: "" };
const EMPTY_COUPON = { code: "", type: "percentage", value: "", min_order: "0", max_discount: "", expiry_date: "", usage_limit: "0", is_active: true };

export default function AdminPanel() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderFilter, setOrderFilter] = useState("");

  // Product form
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT);
  const [savingProduct, setSavingProduct] = useState(false);
  const [productImages, setProductImages] = useState([]);
  const [compressingProductImage, setCompressingProductImage] = useState(false);

  // Category form
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState(EMPTY_CATEGORY);
  const [savingCategory, setSavingCategory] = useState(false);
  const [compressingCategoryImage, setCompressingCategoryImage] = useState(false);
  const [categoryImageSize, setCategoryImageSize] = useState(null);
  const [categoryPreview, setCategoryPreview] = useState("");
  const [categoryImageBlob, setCategoryImageBlob] = useState(null);

  // Coupon form
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState(EMPTY_COUPON);
  const [savingCoupon, setSavingCoupon] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/notifications`, { withCredentials: true });
      const items = data || [];
      setNotifications(items);
      setUnreadCount(items.filter((item) => !item.read).length);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/dashboard`, { withCredentials: true });
      setStats(data);
      setUnreadCount(data.unread_notifications || 0);
    }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = orderFilter ? `?status=${orderFilter}` : "";
      const { data } = await axios.get(`${API}/admin/orders${params}`, { withCredentials: true });
      setOrders(data.orders || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [orderFilter]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/products?limit=100`); setProducts(data.products || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/categories`); setCategories(data || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  const loadCoupons = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/coupons`, { withCredentials: true }); setCoupons(data || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true }); setCustomers(data || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadNotifications();
    const timer = setInterval(loadNotifications, 30000);
    return () => clearInterval(timer);
  }, [loadNotifications]);

  useEffect(() => {
    if (tab === "dashboard") loadDashboard();
    else if (tab === "orders") loadOrders();
    else if (tab === "products") loadProducts();
    else if (tab === "categories") loadCategories();
    else if (tab === "coupons") loadCoupons();
    else if (tab === "customers") loadCustomers();
  }, [tab, loadDashboard, loadOrders, loadProducts, loadCategories, loadCoupons, loadCustomers]);

  const handleStatusChange = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}`, { status }, { withCredentials: true });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status } : o));
    } catch (e) { alert("Failed to update status"); }
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.read) {
        await axios.put(`${API}/admin/notifications/${notification.id}/read`, {}, { withCredentials: true });
      }
      setShowNotifications(false);
      setTab("orders");
      loadOrders();
      loadNotifications();
    } catch (e) {
      console.error(e);
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await axios.put(`${API}/admin/notifications/read-all`, {}, { withCredentials: true });
      loadNotifications();
    } catch (e) {
      console.error(e);
    }
  };

  // ── Product CRUD ─────────────────────────────────────────────────────────
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/products/${productId}`, { withCredentials: true });
      setProducts(prev => prev.filter(p => p.id !== productId));
    } catch (e) { alert("Failed to delete"); }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (productImages.length === 0) {
      alert("Please add at least one product image.");
      return;
    }
    const oversized = productImages.find((img) => img.blob && img.blob.size > 150 * 1024);
    if (oversized) {
      alert(`Each image must be under 150KB. One image is ${formatBytes(oversized.blob.size)}.`);
      return;
    }

    setSavingProduct(true);
    const formData = new FormData();
    formData.append("name", productForm.name.trim());
    formData.append("description", productForm.description.trim());
    formData.append("price", productForm.price);
    formData.append("original_price", productForm.original_price || "");
    formData.append("category", productForm.category);
    formData.append("stock", productForm.stock || "10");
    formData.append("tags", productForm.tags || "");
    formData.append("occasion", productForm.occasion || "");
    formData.append("is_featured", String(productForm.is_featured));
    formData.append("is_bestseller", String(productForm.is_bestseller));
    formData.append("is_new", String(productForm.is_new));
    formData.append("customizable", String(productForm.customizable));
    formData.append(
      "keep_images",
      JSON.stringify(productImages.filter((img) => !img.blob).map((img) => img.preview))
    );
    productImages.filter((img) => img.blob).forEach((img, index) => {
      formData.append("images", img.blob, `product-${index}.jpg`);
    });

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/products`, formData, { withCredentials: true });
      }
      loadProducts();
      closeProductForm();
    } catch (err) {
      if (err.response?.status === 413) {
        alert("Image is too large. Each photo must be under 150KB after compression.");
      } else {
        alert(err.response?.data?.detail || "Failed to save product");
      }
    } finally {
      setSavingProduct(false);
    }
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name, description: product.description,
      price: String(product.price), original_price: String(product.original_price || ""),
      category: product.category, stock: String(product.stock),
      tags: (product.tags || []).join(", "), occasion: product.occasion || "",
      is_featured: product.is_featured, is_bestseller: product.is_bestseller,
      is_new: product.is_new, customizable: product.customizable,
    });
    setProductImages((product.images || []).map((url, index) => ({
      id: `existing-${index}-${url.slice(0, 12)}`,
      preview: url,
      blob: null,
    })));
    setShowProductForm(true);
  };

  const handleProductImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = MAX_PRODUCT_IMAGES - productImages.length;
    if (remaining <= 0) {
      alert(`Maximum ${MAX_PRODUCT_IMAGES} images allowed per product.`);
      e.target.value = "";
      return;
    }

    setCompressingProductImage(true);
    try {
      const nextImages = [...productImages];
      for (const file of files.slice(0, remaining)) {
        const dataUrl = await compressImageFile(file);
        const blob = dataUrlToBlob(dataUrl);
        nextImages.push({
          id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          preview: dataUrl,
          blob,
        });
      }
      setProductImages(nextImages);
    } catch (err) {
      alert(err.message || "Failed to process image");
    } finally {
      setCompressingProductImage(false);
      e.target.value = "";
    }
  };

  const removeProductImage = (imageId) => {
    setProductImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  const closeProductForm = () => {
    setShowProductForm(false);
    setEditingProduct(null);
    setProductForm(EMPTY_PRODUCT);
    setProductImages([]);
  };

  // ── Category CRUD ─────────────────────────────────────────────────────────
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (categoryImageBlob instanceof Blob && categoryImageBlob.size > 150 * 1024) {
      alert(`Image is ${formatBytes(categoryImageBlob.size)} — must be under 150KB. Please re-upload a smaller image.`);
      return;
    }

    setSavingCategory(true);
    const formData = new FormData();
    formData.append("name", categoryForm.name.trim());
    formData.append("description", categoryForm.description.trim());

    if (categoryImageBlob instanceof Blob) {
      formData.append("image", categoryImageBlob, "category.jpg");
    } else if (categoryImageBlob === false) {
      formData.append("remove_image", "true");
    }

    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/categories`, formData, { withCredentials: true });
      }
      loadCategories();
      closeCategoryForm();
    } catch (err) {
      if (err.response?.status === 413) {
        alert("Image is too large. Please upload a smaller photo (max 150KB after compression).");
      } else {
        alert(err.response?.data?.detail || "Failed to save category");
      }
    } finally {
      setSavingCategory(false);
    }
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({ name: cat.name, description: cat.description || "", image: cat.image || "" });
    setCategoryPreview(cat.image || "");
    setCategoryImageBlob(null);
    setCategoryImageSize(cat.image?.startsWith("data:image/") ? dataUrlByteSize(cat.image) : null);
    setShowCategoryForm(true);
  };

  const handleCategoryImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCompressingCategoryImage(true);
    try {
      const dataUrl = await compressImageFile(file);
      const blob = dataUrlToBlob(dataUrl);
      setCategoryImageBlob(blob);
      setCategoryPreview(dataUrl);
      setCategoryImageSize(blob.size);
    } catch (err) {
      alert(err.message || "Failed to process image");
    } finally {
      setCompressingCategoryImage(false);
      e.target.value = "";
    }
  };

  const removeCategoryImage = () => {
    setCategoryImageBlob(false);
    setCategoryPreview("");
    setCategoryImageSize(null);
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await axios.delete(`${API}/categories/${catId}`, { withCredentials: true });
      setCategories(prev => prev.filter(c => c.id !== catId));
    } catch (e) { alert("Failed to delete category"); }
  };

  const closeCategoryForm = () => {
    setShowCategoryForm(false);
    setEditingCategory(null);
    setCategoryForm(EMPTY_CATEGORY);
    setCategoryImageSize(null);
    setCategoryPreview("");
    setCategoryImageBlob(null);
  };

  // ── Coupon CRUD ───────────────────────────────────────────────────────────
  const handleCouponSubmit = async (e) => {
    e.preventDefault(); setSavingCoupon(true);
    try {
      const payload = {
        ...couponForm,
        value: parseFloat(couponForm.value),
        min_order: parseFloat(couponForm.min_order || 0),
        max_discount: couponForm.max_discount ? parseFloat(couponForm.max_discount) : null,
        usage_limit: parseInt(couponForm.usage_limit || 0),
      };
      if (editingCoupon) {
        await axios.put(`${API}/coupons/${editingCoupon.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/coupons`, payload, { withCredentials: true });
      }
      loadCoupons(); closeCouponForm();
    } catch (e) { alert(e.response?.data?.detail || "Failed to save coupon"); }
    finally { setSavingCoupon(false); }
  };

  const openEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setCouponForm({
      code: coupon.code, type: coupon.type, value: String(coupon.value),
      min_order: String(coupon.min_order || 0), max_discount: String(coupon.max_discount || ""),
      expiry_date: coupon.expiry_date ? coupon.expiry_date.split("T")[0] : "",
      usage_limit: String(coupon.usage_limit || 0), is_active: coupon.is_active,
    });
    setShowCouponForm(true);
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm("Delete this coupon?")) return;
    try {
      await axios.delete(`${API}/coupons/${couponId}`, { withCredentials: true });
      setCoupons(prev => prev.filter(c => c.id !== couponId));
    } catch (e) { alert("Failed to delete coupon"); }
  };

  const toggleCouponActive = async (coupon) => {
    try {
      await axios.put(`${API}/coupons/${coupon.id}`, { is_active: !coupon.is_active }, { withCredentials: true });
      setCoupons(prev => prev.map(c => c.id === coupon.id ? { ...c, is_active: !c.is_active } : c));
    } catch (e) { alert("Failed to update coupon"); }
  };

  const closeCouponForm = () => { setShowCouponForm(false); setEditingCoupon(null); setCouponForm(EMPTY_COUPON); };

  const TABS = [
    { id: "dashboard", label: "Dashboard", icon: TrendingUp },
    { id: "products", label: "Products", icon: ShoppingBag },
    { id: "categories", label: "Categories", icon: Grid },
    { id: "coupons", label: "Coupons", icon: Tag },
    { id: "orders", label: "Orders", icon: Package },
    { id: "customers", label: "Customers", icon: Users },
  ];

  return (
    <div className="min-h-screen bg-petal-beige" data-testid="admin-panel">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-60 min-h-screen bg-petal-olive-text shrink-0 sticky top-0 self-start">
          <div className="p-6 border-b border-white/10">
            <Logo className="h-12 mb-3" testId="admin-logo" />
            <p className="font-playfair text-lg text-petal-beige">Admin Panel</p>
            <div className="relative mt-4">
              <button
                type="button"
                onClick={() => setShowNotifications((open) => !open)}
                data-testid="admin-notifications-btn"
                className="relative flex items-center gap-2 w-full px-3 py-2 rounded-sm bg-white/10 text-white/90 hover:bg-white/15 transition-colors"
              >
                <Bell className="w-4 h-4" />
                <span className="font-outfit text-sm">Notifications</span>
                {unreadCount > 0 && (
                  <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-petal-gold text-petal-olive-text text-xs font-semibold flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <div className="absolute left-0 right-0 top-full mt-2 z-20 bg-white border border-petal-border shadow-lg max-h-80 overflow-y-auto">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-petal-border">
                    <p className="font-outfit text-xs uppercase tracking-widest text-petal-muted">Alerts</p>
                    {unreadCount > 0 && (
                      <button type="button" onClick={handleMarkAllNotificationsRead}
                        className="font-outfit text-xs text-petal-olive hover:underline">
                        Mark all read
                      </button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <p className="font-outfit text-sm text-petal-muted p-4">No notifications yet</p>
                  ) : (
                    notifications.slice(0, 10).map((notification) => (
                      <button
                        key={notification.id}
                        type="button"
                        onClick={() => handleNotificationClick(notification)}
                        className={`w-full text-left px-3 py-3 border-b border-petal-border hover:bg-petal-beige transition-colors ${notification.read ? "opacity-70" : "bg-petal-beige-alt"}`}
                      >
                        <p className="font-outfit text-sm font-semibold text-petal-olive-text">{notification.title}</p>
                        <p className="font-outfit text-xs text-petal-muted line-clamp-2 mt-1 whitespace-pre-line">{notification.message}</p>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
          <nav className="p-4 space-y-1">
            {TABS.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 font-outfit text-sm transition-colors rounded-sm ${tab === t.id ? "bg-petal-olive text-white" : "text-white/60 hover:bg-petal-olive/30 hover:text-white"}`}>
                  <Icon className="w-4 h-4 shrink-0" /> {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main */}
        <main className="flex-1 p-8 min-w-0">
          <AnimatePresence mode="wait">
            {/* Dashboard */}
            {tab === "dashboard" && (
              <motion.div key="dashboard" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <h1 className="font-playfair text-3xl text-petal-olive-text mb-8">Dashboard Overview</h1>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {[
                    { label: "Total Revenue", value: `₹${(stats.total_revenue || 0).toLocaleString()}`, icon: TrendingUp, color: "bg-petal-gold/10 border-petal-gold/30" },
                    { label: "Total Orders", value: stats.total_orders || 0, icon: Package, color: "bg-blue-50 border-blue-200" },
                    { label: "Customers", value: stats.total_customers || 0, icon: Users, color: "bg-purple-50 border-purple-200" },
                    { label: "Products", value: stats.total_products || 0, icon: ShoppingBag, color: "bg-green-50 border-green-200" },
                  ].map(s => {
                    const Icon = s.icon;
                    return (
                      <div key={s.label} className={`bg-white border p-6 ${s.color}`} data-testid={`stat-card`}>
                        <Icon className="w-6 h-6 text-petal-olive mb-3" />
                        <p className="font-playfair text-3xl text-petal-olive-text mb-1">{s.value}</p>
                        <p className="font-outfit text-xs uppercase tracking-widest text-petal-muted">{s.label}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="bg-white border border-petal-border p-6">
                  <h2 className="font-playfair text-xl text-petal-olive-text mb-4">Recent Orders</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full font-outfit text-sm">
                      <thead><tr className="border-b border-petal-border text-petal-muted text-xs uppercase tracking-widest">
                        <th className="text-left py-2 pr-4">Order #</th>
                        <th className="text-left py-2 pr-4">Customer</th>
                        <th className="text-left py-2 pr-4">Amount</th>
                        <th className="text-left py-2">Status</th>
                      </tr></thead>
                      <tbody>
                        {(stats.recent_orders || []).map(o => (
                          <tr key={o.id} className="border-b border-petal-border/50 hover:bg-petal-beige-alt">
                            <td className="py-3 pr-4 font-medium text-petal-olive">{o.order_number}</td>
                            <td className="py-3 pr-4">{o.user_name}</td>
                            <td className="py-3 pr-4 font-medium">₹{o.total?.toLocaleString()}</td>
                            <td className="py-3"><span className={`px-2 py-0.5 text-xs rounded-sm ${STATUS_COLORS[o.status]}`}>{o.status}</span></td>
                          </tr>
                        ))}
                        {(!stats.recent_orders || stats.recent_orders.length === 0) && (
                          <tr><td colSpan={4} className="text-center py-8 text-petal-muted font-outfit text-sm">No orders yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Products */}
            {tab === "products" && (
              <motion.div key="products" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <div className="flex justify-between items-center mb-6">
                  <h1 className="font-playfair text-3xl text-petal-olive-text">Products ({products.length})</h1>
                  <button onClick={() => { setShowProductForm(true); setEditingProduct(null); setProductForm(EMPTY_PRODUCT); setProductImages([]); }}
                    data-testid="add-product-btn" className="btn-primary text-xs py-2.5 flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Add Product
                  </button>
                </div>

                {showProductForm && (
                  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-petal-border shadow-2xl">
                      <div className="flex justify-between items-center p-6 border-b border-petal-border">
                        <h2 className="font-playfair text-xl text-petal-olive-text">{editingProduct ? "Edit Product" : "Add New Product"}</h2>
                        <button onClick={closeProductForm}><X className="w-5 h-5" /></button>
                      </div>
                      <form onSubmit={handleProductSubmit} className="p-6 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <label className="admin-label">Product Name *</label>
                            <input required value={productForm.name} onChange={e => setProductForm({ ...productForm, name: e.target.value })}
                              data-testid="product-name-input" className="admin-input" />
                          </div>
                          <div className="col-span-2">
                            <label className="admin-label">Description *</label>
                            <textarea required value={productForm.description} onChange={e => setProductForm({ ...productForm, description: e.target.value })}
                              rows={3} className="admin-input resize-none" />
                          </div>
                          <div>
                            <label className="admin-label">Price (₹) *</label>
                            <input required type="number" min="0" value={productForm.price} onChange={e => setProductForm({ ...productForm, price: e.target.value })}
                              data-testid="product-price-input" className="admin-input" />
                          </div>
                          <div>
                            <label className="admin-label">Original Price (₹)</label>
                            <input type="number" min="0" value={productForm.original_price} onChange={e => setProductForm({ ...productForm, original_price: e.target.value })}
                              className="admin-input" />
                          </div>
                          <div>
                            <label className="admin-label">Category *</label>
                            <select required value={productForm.category} onChange={e => setProductForm({ ...productForm, category: e.target.value })}
                              data-testid="product-category-select" className="admin-input">
                              <option value="">Select Category</option>
                              {(categories.length ? categories.map(c => c.name) : HARDCODED_CATEGORIES).map(c => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="admin-label">Stock</label>
                            <input type="number" min="0" value={productForm.stock} onChange={e => setProductForm({ ...productForm, stock: e.target.value })}
                              className="admin-input" />
                          </div>
                          <div>
                            <label className="admin-label">Occasion</label>
                            <input value={productForm.occasion} onChange={e => setProductForm({ ...productForm, occasion: e.target.value })}
                              placeholder="Birthday, Wedding, etc." className="admin-input" />
                          </div>
                          <div>
                            <label className="admin-label">Tags (comma-separated)</label>
                            <input value={productForm.tags} onChange={e => setProductForm({ ...productForm, tags: e.target.value })}
                              placeholder="birthday, gift, chocolate" className="admin-input" />
                          </div>
                          <div className="col-span-2">
                            <label className="admin-label">Product Images (max {MAX_PRODUCT_IMAGES}, 150KB each)</label>
                            {productImages.length > 0 && (
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                                {productImages.map((img) => (
                                  <div key={img.id} className="relative border border-petal-border">
                                    <img src={img.preview} alt="" className="w-full h-28 object-cover" />
                                    <button type="button" onClick={() => removeProductImage(img.id)}
                                      className="absolute top-1 right-1 bg-white/90 p-1 text-red-500 hover:bg-red-500 hover:text-white">
                                      <X className="w-3 h-3" />
                                    </button>
                                    {img.blob && (
                                      <p className="font-outfit text-[10px] text-petal-muted px-2 py-1">{formatBytes(img.blob.size)}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            {productImages.length < MAX_PRODUCT_IMAGES && (
                              <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-petal-border p-5 cursor-pointer hover:border-petal-olive transition-colors">
                                <span className="font-outfit text-sm text-petal-muted">
                                  {compressingProductImage ? "Compressing..." : "Click to upload images"}
                                </span>
                                <span className="font-outfit text-xs text-petal-muted">
                                  JPG, PNG, WebP — auto-compressed to 150KB max each
                                </span>
                                <input type="file" accept="image/*" multiple className="hidden"
                                  onChange={handleProductImageUpload} disabled={compressingProductImage} />
                              </label>
                            )}
                          </div>
                          <div className="col-span-2 flex flex-wrap gap-4">
                            {[["is_featured","Featured"],["is_bestseller","Bestseller"],["is_new","New Arrival"],["customizable","Customizable"]].map(([field, label]) => (
                              <label key={field} className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={productForm[field]} onChange={e => setProductForm({ ...productForm, [field]: e.target.checked })}
                                  className="w-4 h-4 accent-petal-olive" />
                                <span className="font-outfit text-sm text-petal-olive-text">{label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-3 pt-2">
                          <button type="submit" disabled={savingProduct} data-testid="save-product-btn"
                            className="btn-primary text-xs py-2.5 px-6 disabled:opacity-50">
                            {savingProduct ? "Saving..." : editingProduct ? "Update Product" : "Add Product"}
                          </button>
                          <button type="button" onClick={closeProductForm} className="btn-outline text-xs py-2.5 px-6">Cancel</button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                <div className="bg-white border border-petal-border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full font-outfit text-sm" data-testid="products-table">
                      <thead><tr className="bg-petal-beige-alt border-b border-petal-border text-petal-muted text-xs uppercase tracking-widest">
                        <th className="text-left py-3 px-4">Product</th>
                        <th className="text-left py-3 px-4">Category</th>
                        <th className="text-left py-3 px-4">Price</th>
                        <th className="text-left py-3 px-4">Stock</th>
                        <th className="text-left py-3 px-4">Flags</th>
                        <th className="text-left py-3 px-4">Actions</th>
                      </tr></thead>
                      <tbody>
                        {loading ? (
                          <tr><td colSpan={6} className="text-center py-8 text-petal-muted">Loading...</td></tr>
                        ) : products.map(p => (
                          <tr key={p.id} className="border-b border-petal-border/50 hover:bg-petal-beige-alt" data-testid={`product-row-${p.id}`}>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                {p.images?.[0] && <img src={p.images[0]} alt="" className="w-10 h-10 object-cover border border-petal-border" />}
                                <span className="font-medium text-petal-olive-text line-clamp-1 max-w-36">{p.name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-petal-muted text-xs">{p.category}</td>
                            <td className="py-3 px-4 font-medium">₹{p.price?.toLocaleString()}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-0.5 text-xs ${p.stock > 5 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{p.stock}</span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex flex-wrap gap-1">
                                {p.is_featured && <span className="text-xs bg-petal-gold/20 text-petal-gold px-1.5 py-0.5">Featured</span>}
                                {p.is_bestseller && <span className="text-xs bg-petal-olive/20 text-petal-olive px-1.5 py-0.5">Best</span>}
                                {p.is_new && <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5">New</span>}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex gap-2">
                                <button onClick={() => openEditProduct(p)} data-testid={`edit-product-${p.id}`}
                                  className="w-8 h-8 flex items-center justify-center text-petal-olive hover:bg-petal-beige-alt transition-colors">
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDeleteProduct(p.id)} data-testid={`delete-product-${p.id}`}
                                  className="w-8 h-8 flex items-center justify-center text-red-400 hover:bg-red-50 transition-colors">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Categories */}
            {tab === "categories" && (
              <motion.div key="categories" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <div className="flex justify-between items-center mb-6">
                  <h1 className="font-playfair text-3xl text-petal-olive-text">Categories ({categories.length})</h1>
                  <button onClick={() => { setShowCategoryForm(true); setEditingCategory(null); setCategoryForm(EMPTY_CATEGORY); }}
                    data-testid="add-category-btn" className="btn-primary text-xs py-2.5 flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Add Category
                  </button>
                </div>

                {showCategoryForm && (
                  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-md border border-petal-border shadow-2xl">
                      <div className="flex justify-between items-center p-6 border-b border-petal-border">
                        <h2 className="font-playfair text-xl text-petal-olive-text">{editingCategory ? "Edit Category" : "Add Category"}</h2>
                        <button onClick={closeCategoryForm}><X className="w-5 h-5" /></button>
                      </div>
                      <form onSubmit={handleCategorySubmit} className="p-6 space-y-4">
                        <div>
                          <label className="admin-label">Category Name *</label>
                          <input required value={categoryForm.name} onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })}
                            data-testid="category-name-input" className="admin-input" />
                        </div>
                        <div>
                          <label className="admin-label">Description</label>
                          <textarea value={categoryForm.description} onChange={e => setCategoryForm({ ...categoryForm, description: e.target.value })}
                            rows={2} className="admin-input resize-none" />
                        </div>
                        <div>
                          <label className="admin-label">Category Image (max 150KB)</label>
                          {categoryPreview ? (
                            <div className="space-y-2">
                              <img src={categoryPreview} alt="Preview" className="w-full h-36 object-cover border border-petal-border" />
                              {categoryImageSize != null && (
                                <p className="font-outfit text-xs text-petal-muted">Compressed size: {formatBytes(categoryImageSize)}</p>
                              )}
                              <div className="flex gap-2">
                                <label className="btn-outline text-xs py-2 px-4 cursor-pointer">
                                  Replace
                                  <input type="file" accept="image/*" className="hidden" onChange={handleCategoryImageUpload} disabled={compressingCategoryImage} />
                                </label>
                                <button type="button" onClick={removeCategoryImage} className="text-xs font-outfit text-red-500 hover:underline">Remove</button>
                              </div>
                            </div>
                          ) : (
                            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-petal-border p-6 cursor-pointer hover:border-petal-olive transition-colors">
                              <span className="font-outfit text-sm text-petal-muted">
                                {compressingCategoryImage ? "Compressing..." : "Click to upload image"}
                              </span>
                              <span className="font-outfit text-xs text-petal-muted">JPG, PNG, WebP — auto-compressed to 150KB max</span>
                              <input type="file" accept="image/*" className="hidden" onChange={handleCategoryImageUpload} disabled={compressingCategoryImage} />
                            </label>
                          )}
                        </div>
                        <div className="flex gap-3 pt-2">
                          <button type="submit" disabled={savingCategory} data-testid="save-category-btn"
                            className="btn-primary text-xs py-2.5 px-6 disabled:opacity-50">
                            {savingCategory ? "Saving..." : editingCategory ? "Update" : "Add Category"}
                          </button>
                          <button type="button" onClick={closeCategoryForm} className="btn-outline text-xs py-2.5 px-6">Cancel</button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="categories-grid">
                  {loading ? (
                    <p className="col-span-3 text-center py-8 text-petal-muted font-outfit">Loading...</p>
                  ) : categories.map(cat => (
                    <div key={cat.id} className="bg-white border border-petal-border p-5 flex flex-col gap-2" data-testid={`category-card-${cat.id}`}>
                      {cat.image && <img src={cat.image} alt={cat.name} className="w-full h-32 object-cover border border-petal-border mb-1" />}
                      <p className="font-playfair text-lg text-petal-olive-text">{cat.name}</p>
                      <p className="font-outfit text-xs text-petal-muted line-clamp-2">{cat.description}</p>
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => openEditCategory(cat)} data-testid={`edit-category-${cat.id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-outfit text-petal-olive border border-petal-olive/30 hover:bg-petal-olive hover:text-white transition-colors">
                          <Edit2 className="w-3 h-3" /> Edit
                        </button>
                        <button onClick={() => handleDeleteCategory(cat.id)} data-testid={`delete-category-${cat.id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-outfit text-red-500 border border-red-200 hover:bg-red-500 hover:text-white transition-colors">
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Coupons */}
            {tab === "coupons" && (
              <motion.div key="coupons" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <div className="flex justify-between items-center mb-6">
                  <h1 className="font-playfair text-3xl text-petal-olive-text">Discount Coupons ({coupons.length})</h1>
                  <button onClick={() => { setShowCouponForm(true); setEditingCoupon(null); setCouponForm(EMPTY_COUPON); }}
                    data-testid="add-coupon-btn" className="btn-primary text-xs py-2.5 flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Add Coupon
                  </button>
                </div>

                {showCouponForm && (
                  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-lg border border-petal-border shadow-2xl max-h-[90vh] overflow-y-auto">
                      <div className="flex justify-between items-center p-6 border-b border-petal-border">
                        <h2 className="font-playfair text-xl text-petal-olive-text">{editingCoupon ? "Edit Coupon" : "Create Coupon"}</h2>
                        <button onClick={closeCouponForm}><X className="w-5 h-5" /></button>
                      </div>
                      <form onSubmit={handleCouponSubmit} className="p-6 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <label className="admin-label">Coupon Code *</label>
                            <input required value={couponForm.code} onChange={e => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                              placeholder="e.g. SAVE20" data-testid="coupon-code-input" className="admin-input font-mono uppercase" />
                          </div>
                          <div>
                            <label className="admin-label">Discount Type *</label>
                            <select required value={couponForm.type} onChange={e => setCouponForm({ ...couponForm, type: e.target.value })}
                              data-testid="coupon-type-select" className="admin-input">
                              <option value="percentage">Percentage (%)</option>
                              <option value="flat">Flat Amount (₹)</option>
                            </select>
                          </div>
                          <div>
                            <label className="admin-label">Discount Value *</label>
                            <input required type="number" min="0" value={couponForm.value} onChange={e => setCouponForm({ ...couponForm, value: e.target.value })}
                              data-testid="coupon-value-input" className="admin-input" />
                          </div>
                          <div>
                            <label className="admin-label">Min. Order Amount (₹)</label>
                            <input type="number" min="0" value={couponForm.min_order} onChange={e => setCouponForm({ ...couponForm, min_order: e.target.value })}
                              className="admin-input" />
                          </div>
                          <div>
                            <label className="admin-label">Max Discount (₹) — for %</label>
                            <input type="number" min="0" value={couponForm.max_discount} onChange={e => setCouponForm({ ...couponForm, max_discount: e.target.value })}
                              placeholder="Leave blank for no cap" className="admin-input" />
                          </div>
                          <div>
                            <label className="admin-label">Expiry Date</label>
                            <input type="date" value={couponForm.expiry_date} onChange={e => setCouponForm({ ...couponForm, expiry_date: e.target.value })}
                              className="admin-input" />
                          </div>
                          <div>
                            <label className="admin-label">Usage Limit (0 = unlimited)</label>
                            <input type="number" min="0" value={couponForm.usage_limit} onChange={e => setCouponForm({ ...couponForm, usage_limit: e.target.value })}
                              className="admin-input" />
                          </div>
                          <div className="col-span-2 flex items-center gap-3">
                            <input type="checkbox" id="coupon-active" checked={couponForm.is_active}
                              onChange={e => setCouponForm({ ...couponForm, is_active: e.target.checked })}
                              className="w-4 h-4 accent-petal-olive" />
                            <label htmlFor="coupon-active" className="font-outfit text-sm text-petal-olive-text cursor-pointer">Active</label>
                          </div>
                        </div>
                        <div className="flex gap-3 pt-2">
                          <button type="submit" disabled={savingCoupon} data-testid="save-coupon-btn"
                            className="btn-primary text-xs py-2.5 px-6 disabled:opacity-50">
                            {savingCoupon ? "Saving..." : editingCoupon ? "Update Coupon" : "Create Coupon"}
                          </button>
                          <button type="button" onClick={closeCouponForm} className="btn-outline text-xs py-2.5 px-6">Cancel</button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                <div className="bg-white border border-petal-border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full font-outfit text-sm" data-testid="coupons-table">
                      <thead><tr className="bg-petal-beige-alt border-b border-petal-border text-petal-muted text-xs uppercase tracking-widest">
                        <th className="text-left py-3 px-4">Code</th>
                        <th className="text-left py-3 px-4">Type</th>
                        <th className="text-left py-3 px-4">Discount</th>
                        <th className="text-left py-3 px-4">Min Order</th>
                        <th className="text-left py-3 px-4">Uses</th>
                        <th className="text-left py-3 px-4">Expiry</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Actions</th>
                      </tr></thead>
                      <tbody>
                        {loading ? (
                          <tr><td colSpan={8} className="text-center py-8 text-petal-muted">Loading...</td></tr>
                        ) : coupons.length === 0 ? (
                          <tr><td colSpan={8} className="text-center py-8 text-petal-muted">No coupons yet. Create your first discount!</td></tr>
                        ) : coupons.map(c => (
                          <tr key={c.id} className="border-b border-petal-border/50 hover:bg-petal-beige-alt" data-testid={`coupon-row-${c.id}`}>
                            <td className="py-3 px-4 font-mono font-bold text-petal-olive tracking-wider">{c.code}</td>
                            <td className="py-3 px-4 capitalize text-petal-muted">{c.type}</td>
                            <td className="py-3 px-4 font-medium">{c.type === "percentage" ? `${c.value}%` : `₹${c.value}`}</td>
                            <td className="py-3 px-4 text-petal-muted">₹{c.min_order || 0}</td>
                            <td className="py-3 px-4 text-petal-muted">{c.used_count || 0}{c.usage_limit ? `/${c.usage_limit}` : ""}</td>
                            <td className="py-3 px-4 text-petal-muted text-xs">{c.expiry_date ? new Date(c.expiry_date).toLocaleDateString("en-IN") : "No expiry"}</td>
                            <td className="py-3 px-4">
                              <button onClick={() => toggleCouponActive(c)} data-testid={`toggle-coupon-${c.id}`}
                                className={`flex items-center gap-1.5 text-xs font-outfit px-2 py-1 rounded-sm transition-colors ${c.is_active ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                                {c.is_active ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                                {c.is_active ? "Active" : "Inactive"}
                              </button>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex gap-2">
                                <button onClick={() => openEditCoupon(c)} data-testid={`edit-coupon-${c.id}`}
                                  className="w-8 h-8 flex items-center justify-center text-petal-olive hover:bg-petal-beige-alt transition-colors">
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDeleteCoupon(c.id)} data-testid={`delete-coupon-${c.id}`}
                                  className="w-8 h-8 flex items-center justify-center text-red-400 hover:bg-red-50 transition-colors">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Orders */}
            {tab === "orders" && (
              <motion.div key="orders" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
                  <h1 className="font-playfair text-3xl text-petal-olive-text">All Orders</h1>
                  <select value={orderFilter} onChange={e => setOrderFilter(e.target.value)} data-testid="order-status-filter"
                    className="px-4 py-2 border border-petal-border bg-white font-outfit text-sm focus:outline-none focus:border-petal-olive">
                    <option value="">All Status</option>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
                  </select>
                </div>
                <div className="bg-white border border-petal-border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full font-outfit text-sm" data-testid="orders-table">
                      <thead><tr className="bg-petal-beige-alt border-b border-petal-border text-petal-muted text-xs uppercase tracking-widest">
                        <th className="text-left py-3 px-4">Order #</th>
                        <th className="text-left py-3 px-4">Customer</th>
                        <th className="text-left py-3 px-4">Items</th>
                        <th className="text-left py-3 px-4">Total</th>
                        <th className="text-left py-3 px-4">Date</th>
                        <th className="text-left py-3 px-4">Status</th>
                      </tr></thead>
                      <tbody>
                        {loading ? (
                          <tr><td colSpan={6} className="text-center py-8 text-petal-muted">Loading...</td></tr>
                        ) : orders.length === 0 ? (
                          <tr><td colSpan={6} className="text-center py-8 text-petal-muted">No orders found</td></tr>
                        ) : orders.map(o => (
                          <tr key={o.id} className="border-b border-petal-border/50 hover:bg-petal-beige-alt" data-testid={`order-row-${o.id}`}>
                            <td className="py-3 px-4 font-medium text-petal-olive">{o.order_number}</td>
                            <td className="py-3 px-4">
                              <p className="font-medium text-petal-olive-text">{o.user_name}</p>
                              <p className="text-xs text-petal-muted">{o.user_email}</p>
                            </td>
                            <td className="py-3 px-4 text-petal-muted">{o.items?.length} item(s)</td>
                            <td className="py-3 px-4 font-semibold">₹{o.total?.toLocaleString()}</td>
                            <td className="py-3 px-4 text-petal-muted text-xs">{new Date(o.created_at).toLocaleDateString("en-IN")}</td>
                            <td className="py-3 px-4">
                              <select value={o.status} onChange={e => handleStatusChange(o.id, e.target.value)}
                                data-testid={`order-status-${o.id}`}
                                className={`text-xs px-2 py-1.5 border border-petal-border font-outfit focus:outline-none cursor-pointer ${STATUS_COLORS[o.status]}`}>
                                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Customers */}
            {tab === "customers" && (
              <motion.div key="customers" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <h1 className="font-playfair text-3xl text-petal-olive-text mb-6">Customers ({customers.length})</h1>
                <div className="bg-white border border-petal-border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full font-outfit text-sm" data-testid="customers-table">
                      <thead><tr className="bg-petal-beige-alt border-b border-petal-border text-petal-muted text-xs uppercase tracking-widest">
                        <th className="text-left py-3 px-4">Name</th>
                        <th className="text-left py-3 px-4">Email</th>
                        <th className="text-left py-3 px-4">Phone</th>
                        <th className="text-left py-3 px-4">Role</th>
                        <th className="text-left py-3 px-4">Joined</th>
                      </tr></thead>
                      <tbody>
                        {loading ? (
                          <tr><td colSpan={5} className="text-center py-8 text-petal-muted">Loading...</td></tr>
                        ) : customers.length === 0 ? (
                          <tr><td colSpan={5} className="text-center py-8 text-petal-muted">No customers yet</td></tr>
                        ) : customers.map(u => (
                          <tr key={u.id} className="border-b border-petal-border/50 hover:bg-petal-beige-alt">
                            <td className="py-3 px-4 font-medium text-petal-olive-text">{u.name}</td>
                            <td className="py-3 px-4 text-petal-muted">{u.email}</td>
                            <td className="py-3 px-4 text-petal-muted">{u.phone || "-"}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-0.5 text-xs font-medium ${u.role === "admin" ? "bg-petal-olive/20 text-petal-olive" : "bg-blue-100 text-blue-700"}`}>{u.role}</span>
                            </td>
                            <td className="py-3 px-4 text-petal-muted text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN") : "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}

